import React from 'react';
import HoverMenu from 'ats/components/hover-menu/HoverMenu';
import { dialogStore } from 'components/layout/modals/DialogStore';
import { notificationStore } from 'components/layout/notifications/NotificationStore';
import { observer } from 'mobx-react';
import { jobApplicationStore } from 'ats/pages/pipeline/components/JobApplicationStore';
import { applicantStore } from 'ats/pages/applicants/ApplicantStore';
import { drawerStore } from 'ats/components/drawer/DrawerStore';
import UserProfileAvatar from 'ats/components/user-profile-avatar/UserProfileAvatar';
import { eventManager } from 'ats/utils/EventManager';
import ApplicantStageIndicator from 'ats/components/applicant-stage-indicator/ApplicantStageIndicator';
import ApplicantProfile from 'ats/pages/applicants/ApplicantProfile';
import ActionMenu from 'ats/components/action-menu/ActionMenu';
import { copyTextToClipboard } from 'ats/utils/copyToClipboard';
import JobApplicationStageLabel from 'ats/pages/pipeline/components/JobApplicationStageLabel';
import { modalStore } from 'components/layout/modals/ModalStore';
import { JobApplicationTaskCreate } from 'ats/pages/pipeline/components/JobApplicationTaskCreate';
import Dropdown from 'ats/components/dropdown/Dropdown';
import { getJobApplicationStageTheme } from 'ats/constants/jobApplicationStageTheme';

const DIALOG_CONFIGS = {
	DELETE: {
		title: 'Delete Job Application',
		message: 'This Job Application entry will be deleted. Are you sure?',
		confirmLabel: 'Delete',
		confirmButtonClass: 'is-danger'
	}
};

function CandidatesTable({ data, sort, onSort, onSuccess, job }) {
	const getStage = value =>
		jobApplicationStore.stageOptions.find(stage => stage.value === value);

	const getStatus = value =>
		jobApplicationStore.statusOptions.find(status => status.value === value);

	const getApplicantStage = value =>
		applicantStore.stageOptions.find(stage => stage.value === value);

	const getApplicantStatus = value =>
		applicantStore.statusOptions.find(status => status.value === value);

	const isColumnSorted = column => {
		if (!sort || sort.column !== column) return null;
		const icon = sort.asc ? 'arrow-up-icon' : 'arrow-down-icon';
		return (
			<span className="sort-icon">
				<img src={`/ats/icons/${icon}.svg`} alt="" />
			</span>
		);
	};

	const handleHeaderClick = column => {
		onSort?.(column);
	};

	const handleApplicationStageChange = async (row, stage) => {
		await jobApplicationStore.update(row.id, {
			jobApplication: { stage: stage.value }
		});
		onSuccess?.();

		eventManager.emit('jobApplicationStageChanged', {
			jobApplicationId: row.id,
			stage: stage.value,
			applicantId: row.applicant.id,
			jobId: row.job.id
		});

		eventManager.emit('jobApplicationsListReload', {
			jobId: row.job.id
		});
	};

	const getApplicationStageDropdownOptions = () =>
		jobApplicationStore.stageOptions.map(stage => {
			const statusData = getStatus(
				jobApplicationStore.stageToStatus[stage.value]
			);

			return {
				...stage,
				label: (
					<JobApplicationStageLabel stageData={stage} statusData={statusData} />
				)
			};
		});

	const handleJobApplicationDelete = jobApplication => {
		dialogStore.openDialog(DIALOG_CONFIGS.DELETE, () =>
			jobApplicationStore.delete(jobApplication.id).then(result => {
				if (result.status === 'success') {
					notificationStore.pushSuccessNotification(
						'Job Application Deleted',
						'The Job Application has been successfully deleted.'
					);
					onSuccess?.();

					eventManager.emit('jobApplicationDeleted', {
						jobApplicationId: jobApplication.id,
						applicantId: jobApplication.applicant.id
					});

					eventManager.emit('jobApplicationsListReload', {
						jobId: jobApplication.job.id
					});
				} else {
					notificationStore.pushErrorNotification(
						'Delete Failed',
						'Failed to delete the Job Application'
					);
				}
			})
		);
	};

	const handleCreateTask = row => {
		modalStore.openModal(
			{ title: 'Add Task', size: 'xl', position: 'center' },
			<JobApplicationTaskCreate
				handleSuccess={onSuccess}
				applicant={{
					...row.applicant,
					jobApplications: [{ id: row.id, jobId: row.job?.id }]
				}}
				job={job || row.job}
			/>
		);
	};

	const openApplicantProfile = row => {
		drawerStore.openDrawer(
			{
				containerId: ['job-positions', 'pipeline', 'applicants', 'clients'],
				drawerId: drawerStore.DRAWER_IDS.APPLICANT_PROFILE,
				showHeader: false,
				props: {
					applicantId: row.applicant.id,
					onSuccess: () => onSuccess?.()
				}
			},
			ApplicantProfile
		);
	};

	const getMenuItems = row => [
		{
			label: 'Change Application Stage',
			subItems: getApplicationStageDropdownOptions()
				.filter(stage => stage.value !== row?.stage)
				.map(stage => ({
					...stage,
					onClick: () => handleApplicationStageChange(row, stage)
				}))
		},
		{
			label: 'Add Task',
			onClick: () => handleCreateTask(row)
		},
		{
			label: 'Unassign',
			onClick: () => handleJobApplicationDelete(row)
		}
	];

	const renderTableHeader = () => (
		<thead>
			<tr>
				<th style={{ width: '44px' }}></th>
				<th
					className="is-sortable"
					onClick={() => handleHeaderClick('applicant.name')}>
					Name {isColumnSorted('applicant.name')}
				</th>
				<th
					className="is-sortable has-text-centered"
					style={{ width: '15%' }}
					onClick={() => handleHeaderClick('applicant.linkedinUrl')}>
					Linkedin {isColumnSorted('applicant.linkedinUrl')}
				</th>
				<th
					className="is-sortable stage-column"
					onClick={() => handleHeaderClick('stage')}>
					Application Stage {isColumnSorted('stage')}
				</th>
				<th
					className="is-sortable"
					style={{ width: '20%' }}
					onClick={() =>
						handleHeaderClick('applicant.candidate.$expectedCompensation')
					}>
					Expected Compensation{' '}
					{isColumnSorted('applicant.candidate.$expectedCompensation')}
				</th>
				<th className="has-text-centered" style={{ width: '60px' }}></th>
			</tr>
		</thead>
	);

	const renderTableRow = row => {
		const stageData = getStage(row.stage);
		const statusData = getStatus(row.status);
		const applicantStageData = getApplicantStage(row.applicant?.stage);
		const applicantStatusData = getApplicantStatus(row.applicant?.status);
		const linkedinUrl = row.applicant?.linkedinUrl;

		return (
			<tr key={row.id}>
				<td className="profile-avatar-column">
					<UserProfileAvatar
						imageUrl={
							row.applicant?.imageUrl || row.applicant?.candidate?.imageUrl
						}
						name={row.applicant?.name}
						size={24}
						showTooltip={false}
					/>
				</td>
				<td className="name-column">
					<div>
						<ApplicantStageIndicator
							stageLabel={applicantStageData?.label}
							statusLabel={applicantStatusData?.label}
							stageLevel={applicantStageData?.level}
							statusColor={applicantStatusData?.color}
						/>
						<span
							className="applicant-name-link"
							onClick={() => openApplicantProfile(row)}>
							{row.applicant?.name || ''}
						</span>
					</div>
				</td>
				<td className="linkedin-column">
					{linkedinUrl ? (
						<ActionMenu
							buttonIcon={
								<img src="/ats/icons/linkedin-icon.svg" alt="LinkedIn" />
							}
							tooltipText={linkedinUrl}
							items={[
								{
									label: 'Copy LinkedIn URL',
									img: '/ats/icons/copy-icon.svg',
									onClick: () => copyTextToClipboard(linkedinUrl),
									successLabel: 'LinkedIn URL Copied',
									successImg: '/ats/icons/check-icon.svg',
									resetDelay: 2000
								},
								{
									label: 'Open LinkedIn URL',
									img: '/ats/icons/send-icon.svg',
									onClick: () => window.open(linkedinUrl, '_blank')
								}
							]}
						/>
					) : (
						<div className="inactive-icon">
							<img src="/ats/icons/linkedin-icon.svg" alt="LinkedIn" />
						</div>
					)}
				</td>
				<td className="is-capitalized stage-column">
					<Dropdown
						value={{
							...stageData,
							label: (
								<JobApplicationStageLabel
									stageData={stageData}
									statusData={statusData}
									showStatusTooltip
									truncate
								/>
							)
						}}
						buttonTextColor={
							getJobApplicationStageTheme(stageData?.value).color
						}
						menuClassName="job-application-stage-dropdown-menu"
						hideSelectedOption
						onChange={stage => handleApplicationStageChange(row, stage)}
						options={getApplicationStageDropdownOptions()}
					/>
				</td>
				<td className="is-capitalized">
					{row.applicant?.expectedCurrency}{' '}
					{row.applicant?.expectedCompensation}
					{row.applicant?.expectedScheme
						? ` - ${row.applicant?.expectedScheme}`
						: ''}
				</td>
				<td className="has-text-centered action-btn">
					<HoverMenu
						id={row.id}
						position="left"
						buttonIcon={
							<img src="/ats/icons/three-dots-icon.svg" alt="Menu button" />
						}
						items={getMenuItems(row)}
					/>
				</td>
			</tr>
		);
	};

	return (
		<table className="table is-fullwidth">
			{renderTableHeader()}
			<tbody>{data.map(renderTableRow)}</tbody>
		</table>
	);
}

export default observer(CandidatesTable);
